import { createContext, type ReactNode, use, useCallback, useEffect, useRef, useState } from 'react';
import { FilePreviewDialog, type FilePreviewFile } from './file-preview-dialog';

export interface FilePreviewRequest extends FilePreviewFile {
  /**
   * Fetches the URL for a file that has none to hand. The dialog opens on the file first and fills
   * in once this settles, so the wait is in front of the user rather than before it.
   */
  resolveUrl?: () => Promise<string | undefined>;
  /**
   * Saves the file. Without it the dialog offers a plain download link, which only holds for a URL
   * of this origin: a browser ignores `download` on a cross-origin one and navigates to it instead.
   */
  onDownload?: () => void;
}

type OpenPreview = (request: FilePreviewRequest) => Promise<void>;

const FilePreviewContext = createContext<OpenPreview | undefined>(undefined);

/** Shows a file in the one preview dialog the app mounts. */
export const useFilePreview = (): OpenPreview => {
  const openPreview = use(FilePreviewContext);
  if (!openPreview) {
    throw new Error('useFilePreview requires a FilePreviewProvider');
  }
  return openPreview;
};

/**
 * Holds the preview dialog for everything below it. Only one file can be shown at a time, so the
 * dialog is mounted while one is and nowhere near the components that ask for it.
 */
export const FilePreviewProvider = ({ children }: { children: ReactNode }) => {
  const [request, setRequest] = useState<FilePreviewRequest>();
  const [isOpen, setIsOpen] = useState(false);
  const previewDialog = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    if (isOpen && !previewDialog.current?.open) {
      previewDialog.current?.showModal();
    }
  }, [isOpen]);

  const openPreview = useCallback<OpenPreview>(async (next) => {
    setRequest(next);
    setIsOpen(true);

    if (!next.resolveUrl) return;

    const url = await next.resolveUrl();
    setRequest((current) => {
      // A slow resolve must not land on a file opened after it.
      if (current !== next) return current;
      // Nothing to show rather than a frame left loading on a URL that never arrives.
      return url === undefined ? undefined : { ...next, url };
    });
  }, []);

  return (
    <FilePreviewContext value={openPreview}>
      {children}
      {isOpen && (
        <FilePreviewDialog
          ref={previewDialog}
          file={request}
          onDownload={request?.onDownload}
          onClose={() => setIsOpen(false)}
        />
      )}
    </FilePreviewContext>
  );
};
